import { FC } from 'react';

import { useGetDegreesQuery } from '@teachur/http-client';

import { SITUATION_OPTIONS, TARGET_DEGREE_OPTIONS } from '../../../utils/setupOptions';
import { buildPlanMessage } from '../../../utils/planMessage';
import type { WizardValues } from '../../../schemas/wizard.schema';

interface SummaryStepProps {
  values: WizardValues;
}

const Row: FC<{ label: string; value?: string | number | null }> = ({ label, value }) =>
  value == null || value === '' ? null : (
    <div className="flex justify-between gap-4 py-2 text-sm">
      <span className="font-medium text-(--neutral-black-NB1000)">{label}</span>
      <span className="text-(--neutral-black-NB700)">{value}</span>
    </div>
  );

/** Step 4 — read-only recap + "what this means" message. Finish lives in the footer. */
export const SummaryStep: FC<SummaryStepProps> = ({ values }) => {
  const situation = SITUATION_OPTIONS.find((o) => o.value === values.priorEducationStatus);
  const targetDegreeLabel = TARGET_DEGREE_OPTIONS.find(
    (o) => o.value === values.targetDegreeType
  )?.label;
  const { data: degrees = [] } = useGetDegreesQuery();
  const targetProgramName = degrees.find((d) => d.id === values.targetDegreeId)?.name ?? null;
  const plan = buildPlanMessage(values.priorEducationStatus);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="m-0! [font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)!">
          Summary
        </h2>
        <p className="mt-1 [font-size:var(--fs-p3)]! text-(--neutral-black-NB700)!">
          Review your information before finishing.
        </p>
      </div>

      <div className="divide-y divide-(--neutral-black-NB200) rounded-md border border-(--neutral-black-NB200) px-4">
        <Row label="Situation" value={situation?.title} />
        <Row label="Target degree" value={targetDegreeLabel} />
        <Row label="Program you're interested in" value={targetProgramName} />
        <Row label="Approx. credits" value={values.estimatedCredits} />
        <Row label="Credit sources" value={values.creditSourceCount} />
        <Row label="Degree" value={values.degreeName} />
        <Row label="Institution" value={values.degreeInstitution} />
        <Row label="Additional credits" value={values.additionalCredits} />
        <Row label="Former name" value={values.hasNameChange ? values.formerName : null} />
      </div>

      {plan ? (
        <div className="flex flex-col gap-3 rounded-md bg-(--primary-O200) p-4 text-sm text-(--neutral-black-NB1000)">
          <p className="m-0!">{plan.intro}</p>
          <p className="m-0! text-(--neutral-black-NB700)">{plan.nextSteps}</p>
        </div>
      ) : null}
    </div>
  );
};
